'use client';

import React, { useState, useEffect } from 'react';
import IconButton from '@mui/material/IconButton';
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import IconDown from '@mui/icons-material/ThumbDown';
import IconUp from '@mui/icons-material/ThumbUp';
import ErrorBoundary from '../ErrorBoundary';
import { useLocalizationContext } from '../LocalizationContext';

const AI_FEEDBACK_STORAGE_KEY = 'aiSearchFeedback';

export interface AIFeedbackThumbsProps {
  messageId: string;
  apiHost: string;
  label?: string;
  onFeedbackSubmitted?: (feedbackId: string) => void;
}

type FeedbackRating = 'THUMBS_UP' | 'THUMBS_DOWN';

function AIFeedbackThumbs({ messageId, apiHost, label, onFeedbackSubmitted }: AIFeedbackThumbsProps) {
  const localization = useLocalizationContext();
  const [curFeedback, setCurFeedback] = useState<'up' | 'down' | ''>('');
  const [isSubmitting, setIsSubmitting] = useState(false);

  // Load existing feedback from localStorage
  useEffect(() => {
    if (!messageId) return;
    try {
      const stored = localStorage.getItem(AI_FEEDBACK_STORAGE_KEY);
      if (stored) {
        const feedbackObj = JSON.parse(stored);
        const existingFeedback = feedbackObj[messageId] || '';
        setCurFeedback(existingFeedback);
      }
    } catch (err) {
      console.error('Error loading AI feedback from localStorage:', err);
    }
  }, [messageId]);

  const submitFeedback = async (rating: FeedbackRating): Promise<string | null> => {
    if (!messageId || !apiHost) return null;

    try {
      const response = await fetch(`${apiHost}/api/v1/feedback`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          messageId,
          rating
        })
      });

      if (!response.ok) {
        throw new Error(`Feedback API error: ${response.status}`);
      }

      const result = await response.json();
      // Return the feedback ID from the response
      return result.id || null;
    } catch (err) {
      console.error('Error submitting AI feedback:', err);
      return null;
    }
  };

  const handleFeedback = async (feedback: 'up' | 'down') => {
    if (isSubmitting || curFeedback === feedback) return;

    setIsSubmitting(true);

    const rating: FeedbackRating = feedback === 'up' ? 'THUMBS_UP' : 'THUMBS_DOWN';
    const feedbackId = await submitFeedback(rating);

    if (feedbackId) {
      setCurFeedback(feedback);
      onFeedbackSubmitted?.(feedbackId);

      // Track feedback event in GA4
      if (typeof window !== 'undefined' && window.dataLayer) {
        window.dataLayer.push({
          event: feedback === 'up' ? 'ai_search_feedback_up' : 'ai_search_feedback_down',
          isCustomEvent: 1,
          url: window.location?.pathname
        });
      }

      // Save to localStorage for UI state persistence
      try {
        const stored = localStorage.getItem(AI_FEEDBACK_STORAGE_KEY) || '{}';
        const feedbackObj = JSON.parse(stored);
        feedbackObj[messageId] = feedback;
        localStorage.setItem(AI_FEEDBACK_STORAGE_KEY, JSON.stringify(feedbackObj));
      } catch (err) {
        console.error('Error saving AI feedback to localStorage:', err);
      }
    }

    setIsSubmitting(false);
  };

  if (!messageId) return null;

  return (
    <ErrorBoundary>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
        <Typography variant="body2" color="text.secondary">
          {label ||
            localization['aiSearch.wasThisHelpful.label']?.shortTextValue ||
            'Was this helpful?'}
        </Typography>
        <IconButton
          data-testid="AI-Thumbs-Up-Button"
          disableRipple
          size="small"
          disabled={isSubmitting}
          onClick={(e) => {
            e.preventDefault();
            handleFeedback('up');
          }}
          sx={{
            opacity: curFeedback === 'up' ? 1 : 0.5,
            '&:hover': { opacity: 0.8 }
          }}>
          <IconUp fontSize="small" />
        </IconButton>
        <IconButton
          data-testid="AI-Thumbs-Down-Button"
          disableRipple
          size="small"
          disabled={isSubmitting}
          onClick={(e) => {
            e.preventDefault();
            handleFeedback('down');
          }}
          sx={{
            opacity: curFeedback === 'down' ? 1 : 0.5,
            '&:hover': { opacity: 0.8 }
          }}>
          <IconDown fontSize="small" />
        </IconButton>
      </Box>
    </ErrorBoundary>
  );
}

export default AIFeedbackThumbs;
